import './ui/chrome.css';
import { type DicomJson, type InstanceRef, type SeriesRef, tagString } from './dicomweb';
import type { ViewerShell } from './shell';
import { chooseLabelSide, freeWidth, type LabelLayout, type LabelSide } from './labelplace';
import { mammoViewLabel } from './mammo';
import { initTools } from './tools';
import type { Viewport } from './viewport';
import { timedFetch } from './net';
import { projectionWarning } from './spacing';

/** DICOM DA (YYYYMMDD) → YYYY-MM-DD, as the IMEXHS overlay and header show dates. */
export function formatDicomDate(value: string): string {
  return /^\d{8}$/.test(value) ? `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}` : value;
}

/** DICOM TM (HHMMSS.frac) → HH:MM:SS. */
export function formatDicomTime(value: string): string {
  return /^\d{6}/.test(value) ? `${value.slice(0, 2)}:${value.slice(2, 4)}:${value.slice(4, 6)}` : value;
}

// --- Custom tags (spec section 3, "Default custom tag sets") ------------------------------------

export type OverlayTagType = 'string' | 'number' | 'float' | 'date' | 'date_and_time' | 'age_string';

/** One configurable overlay line: `<b>label: </b>value`. An empty label shows the value alone. */
export interface OverlayTag {
  label: string;
  tag: string;
  type: OverlayTagType;
  /** Second tag joined to a `date_and_time` value (the time part). */
  timeTag?: string;
}

/** Code default left set (`customsDisplayTags`): TR, TE, flip angle, echo number. */
export const DEFAULT_LEFT_TAGS: readonly OverlayTag[] = [
  { label: 'RT', tag: '00180080', type: 'number' },
  { label: 'ET', tag: '00180081', type: 'number' },
  { label: 'Flip', tag: '00181314', type: 'number' },
  { label: 'EN', tag: '00180086', type: 'number' },
];

/** Code default right set: patient name (no label), ID, age, series date + time, laterality. */
export const DEFAULT_RIGHT_TAGS: readonly OverlayTag[] = [
  { label: '', tag: '00100010', type: 'string' },
  { label: 'ID', tag: '00100020', type: 'string' },
  { label: 'Age', tag: '00101010', type: 'age_string' },
  { label: 'Date', tag: '00080021', timeTag: '00080031', type: 'date_and_time' },
  { label: 'Laterality', tag: '00200062', type: 'string' },
];

/** Label keys the IMEXHS overlay translates; any other label is shown as `<label>: `. */
const LABEL_TEXT: Record<string, string> = { ID: 'ID: ', Age: ' Edad: ', Laterality: ' Lateralidad: ', Flip: ' Voltear: ' };

const AGE_UNITS: Record<string, string> = { Y: 'años', M: 'meses', W: 'semanas', D: 'días' };

let leftTags: readonly OverlayTag[] = DEFAULT_LEFT_TAGS;
let rightTags: readonly OverlayTag[] = DEFAULT_RIGHT_TAGS;

/** Replaces the custom tag sets (Configuración → Etiqueta propiedades) and redraws every overlay. */
export function setOverlayTags(left: readonly OverlayTag[], right: readonly OverlayTag[]): void {
  leftTags = left;
  rightTags = right;
  refreshAll();
}

export function overlayTags(): { left: readonly OverlayTag[]; right: readonly OverlayTag[] } {
  return { left: leftTags, right: rightTags };
}

// --- Visibility and cine ------------------------------------------------------------------------

let overlayVisible = true;
const cineOn = new WeakSet<Viewport>();
const known = new Set<Viewport>();

/** "(h) Ocultar información DICOM": hides every viewport's overlay text. */
export function setOverlayVisible(visible: boolean): void {
  overlayVisible = visible;
  document.querySelectorAll('.view-labels').forEach((el) => el.classList.toggle('hidden', !visible));
}

export function isOverlayVisible(): boolean {
  return overlayVisible;
}

/**
 * Shows "CINE" in place of the bottom-left block while cine plays on `viewport`.
 * A running cornerstone playClip is also detected on its own; this makes it explicit.
 */
export function setCineIndicator(viewport: Viewport, on: boolean): void {
  if (on) cineOn.add(viewport);
  else cineOn.delete(viewport);
  if (viewport.currentImage()) renderViewLabels(viewport);
}

function isCinePlaying(viewport: Viewport): boolean {
  if (cineOn.has(viewport)) return true;
  const clip = window.cornerstoneTools.getToolState(viewport.element, 'playClip')?.data[0];
  return clip !== undefined && clip.intervalId !== undefined && clip.intervalId !== null;
}

function refreshAll(): void {
  for (const viewport of known) {
    if (!viewport.element.isConnected) known.delete(viewport);
    else if (viewport.currentImage()) renderViewLabels(viewport);
  }
}

// --- Tag access: WADO-RS JSON (fast) or the parsed Part-10 dataset (parity) ---------------------

/** Reads DICOM attributes of the image on screen, whatever the load mode. */
interface TagReader {
  text(tag: string): string;
  numbers(tag: string): number[];
  /** Items of a sequence attribute, each readable the same way. */
  items(tag: string): TagReader[];
}

interface DicomDataSet {
  string(tag: string): string | undefined;
  elements?: Record<string, { items?: Array<{ dataSet?: DicomDataSet }> } | undefined>;
}

interface WadoUriApi {
  parseImageId(imageId: string): { url: string };
  dataSetCacheManager: { get(uri: string): DicomDataSet | undefined };
}

function jsonReader(json: DicomJson | undefined): TagReader {
  return {
    text: (tag) => {
      const values = json?.[tag]?.Value;
      if (!values || values.length === 0) return '';
      if (values.length > 1 && typeof values[0] !== 'object') return values.join('\\');
      return tagString(json, tag);
    },
    numbers: (tag) => (json?.[tag]?.Value ?? []).map(Number).filter(Number.isFinite),
    items: (tag) => ((json?.[tag]?.Value ?? []) as unknown[])
      .filter((item): item is DicomJson => !!item && typeof item === 'object')
      .map((item) => jsonReader(item)),
  };
}

function dataSetReader(dataSet: DicomDataSet | undefined): TagReader {
  const text = (tag: string): string => dataSet?.string(`x${tag.toLowerCase()}`)?.trim() ?? '';
  return {
    text,
    numbers: (tag) => text(tag).split('\\').filter((part) => part.trim() !== '').map(Number).filter(Number.isFinite),
    items: (tag) => (dataSet?.elements?.[`x${tag.toLowerCase()}`]?.items ?? [])
      .map((item) => item.dataSet)
      .filter((item): item is DicomDataSet => !!item)
      .map((item) => dataSetReader(item)),
  };
}

/** Instance holding stack image `index` (frames of multi-frame instances count one by one). */
export function instanceAt(series: SeriesRef, index: number): InstanceRef | undefined {
  let remaining = index;
  for (const instance of series.instances) {
    if (remaining < instance.numberOfFrames) return instance;
    remaining -= instance.numberOfFrames;
  }
  return series.instances[series.instances.length - 1];
}

function dataSetFor(imageId: string): DicomDataSet | undefined {
  if (!imageId.startsWith('wadouri:')) return undefined;
  const wadouri = window.cornerstoneWADOImageLoader.wadouri as unknown as WadoUriApi;
  try {
    return wadouri.dataSetCacheManager.get(wadouri.parseImageId(imageId).url);
  } catch (error) {
    console.warn('[viewer] overlay tags unavailable', error);
    return undefined;
  }
}

/** Instance headers fetched on demand (parity mode with raster frames keeps no dataset). */
const fetchedMetadata = new Map<string, DicomJson | null>();

function fetchInstanceMetadata(viewport: Viewport, series: SeriesRef, instance: InstanceRef): void {
  const uid = instance.sopInstanceUID;
  if (fetchedMetadata.has(uid)) return;
  fetchedMetadata.set(uid, null);
  const { client } = viewport.ctx;
  timedFetch(`${client.instanceUrl(series, instance)}/metadata`, {
    headers: { Accept: 'application/dicom+json', ...client.requestHeaders() },
    credentials: 'same-origin',
  })
    .then(async (response) => {
      if (!response.ok) throw new Error(`metadata ${response.status}`);
      const [json] = (await response.json()) as DicomJson[];
      fetchedMetadata.set(uid, json ?? null);
      if (viewport.element.isConnected && viewport.currentImage()) renderViewLabels(viewport);
    })
    .catch((error: unknown) => console.warn('[viewer] overlay metadata unavailable', uid, error));
}

function readerFor(viewport: Viewport, series: SeriesRef, index: number, imageId: string): TagReader {
  const instance = instanceAt(series, index);
  if (instance?.metadata) return jsonReader(instance.metadata);
  const dataSet = dataSetFor(imageId);
  if (dataSet) return dataSetReader(dataSet);
  if (!instance) return jsonReader(undefined);
  const fetched = fetchedMetadata.get(instance.sopInstanceUID);
  if (fetched) return jsonReader(fetched);
  fetchInstanceMetadata(viewport, series, instance);
  return jsonReader(undefined);
}

/** Reader for any viewport's current image (also used by the header and other chrome). */
export function tagReaderFor(viewport: Viewport): TagReader | undefined {
  const stack = viewport.currentStack;
  const image = viewport.currentImage();
  if (!stack || !image) return undefined;
  return readerFor(viewport, stack.series, viewport.currentIndex() ?? 0, image.imageId);
}

// --- Value formatting ---------------------------------------------------------------------------

/** DICOM AS (nnnD/W/M/Y) → "45 años", "3 meses"… */
export function formatAgeString(value: string): string {
  const match = /^(\d{1,3})\s*([DWMY])$/i.exec(value.trim());
  if (!match) return value;
  return `${Number(match[1])} ${AGE_UNITS[match[2].toUpperCase()]}`;
}

function formatNumber(value: number, type: OverlayTagType): string {
  if (type === 'float') return value.toFixed(2);
  return String(Math.round(value * 1000) / 1000);
}

function tagValue(reader: TagReader, tag: OverlayTag): string {
  const raw = reader.text(tag.tag);
  switch (tag.type) {
    case 'number':
    case 'float': {
      const [n] = reader.numbers(tag.tag);
      return n === undefined ? '' : formatNumber(n, tag.type);
    }
    case 'date':
      return formatDicomDate(raw);
    case 'date_and_time': {
      const time = tag.timeTag ? formatDicomTime(reader.text(tag.timeTag)) : '';
      return [formatDicomDate(raw), time].filter(Boolean).join(' ');
    }
    case 'age_string':
      return formatAgeString(raw);
    default:
      return raw.replace(/\^/g, ' ').replace(/\s+/g, ' ').trim();
  }
}

function labelText(label: string): string {
  if (!label) return '';
  return LABEL_TEXT[label] ?? `${label}: `;
}

// --- Corner content -----------------------------------------------------------------------------

/** A corner line: plain text, or a bold label followed by its value. */
type Line = string | { label: string; value: string };

interface Corners {
  topLeft: Line[];
  topRight: Line[];
  bottomLeft: Line[];
  bottomRight: Line[];
}

// --- Hook: lines other modules add to a corner (src/fusion adds the PET SUV line bottom-left) ---
export type OverlayCorner = keyof Corners;
type LineProvider = (viewport: Viewport) => string[];
const extraLines: Array<{ corner: OverlayCorner; provider: LineProvider }> = [];

export function addOverlayLines(corner: OverlayCorner, provider: LineProvider): void {
  extraLines.push({ corner, provider });
}

function withExtraLines(viewport: Viewport, corners: Corners): Corners {
  for (const { corner, provider } of extraLines) corners[corner] = [...corners[corner], ...provider(viewport)];
  return corners;
}

const FRAME_RATE_MODALITIES = new Set(['XA', 'US']);
const MS_PER_SECOND = 1000;

function customLines(reader: TagReader, tags: readonly OverlayTag[]): Line[] {
  const lines: Line[] = [];
  for (const tag of tags) {
    const value = tagValue(reader, tag);
    if (!value) continue;
    lines.push(tag.label ? { label: labelText(tag.label), value } : value);
  }
  return lines;
}

/** XA / US: "Frame Rate: N FPS" from Recommended Display Frame Rate, else 1000 / Frame Time. */
function frameRateLine(reader: TagReader, modality: string): string {
  if (!FRAME_RATE_MODALITIES.has(modality)) return '';
  const [recommended] = reader.numbers('00082144');
  const [frameTime] = reader.numbers('00181063');
  const fps = recommended > 0 ? recommended : frameTime > 0 ? MS_PER_SECOND / frameTime : 0;
  return fps > 0 ? `Frame Rate: ${Math.round(fps)} FPS` : '';
}

function bottomLeftLines(viewport: Viewport, reader: TagReader, modality: string, index: number, total: number): Line[] {
  if (isCinePlaying(viewport)) return ['CINE'];
  const warning = projectionWarning(modality, reader.numbers('00280030'), reader.numbers('00181164'), reader.numbers('00181114'));
  const [thickness] = reader.numbers('00180050');
  return [warning, `Corte ${index + 1}/${total}`, thickness ? `THK: ${thickness.toFixed(2)} mm` : ''].filter(Boolean);
}

/**
 * W / L in modality units. cornerstone applies the modality LUT (slope / intercept) before the VOI,
 * so the viewport window is already in those units (HU for CT): printing it as-is gives the DICOM
 * window (e.g. 400 / 40), and 255 / 128 for 8-bit rendered JPEGs.
 */
function windowLines(viewport: Viewport): Line[] {
  const vp = window.cornerstone.getViewport(viewport.element);
  if (!vp) return [];
  return [`W: ${Math.round(vp.voi.windowWidth)}`, `L: ${Math.round(vp.voi.windowCenter)}`];
}

function buildCorners(viewport: Viewport, reader: TagReader): Corners | undefined {
  const stack = viewport.currentStack;
  if (!stack) return undefined;
  const { series } = stack;
  const index = viewport.currentIndex() ?? 0;
  const modality = series.modality || reader.text('00080060');
  const description = (series.description || reader.text('0008103E')).toUpperCase();
  const seriesNumber = series.seriesNumber || reader.numbers('00200011')[0];
  const view = modality === 'MG' ? mammoViewLabel(reader) : '';
  return {
    topLeft: [
      ...(view ? [{ label: view, value: '' }] : []),
      description, seriesNumber ? String(seriesNumber) : '', frameRateLine(reader, modality), ...customLines(reader, leftTags),
    ].filter(Boolean),
    topRight: customLines(reader, rightTags),
    bottomLeft: bottomLeftLines(viewport, reader, modality, index, stack.imageIds.length),
    bottomRight: windowLines(viewport),
  };
}

// --- Orientation markers ------------------------------------------------------------------------

export interface EdgeMarkers {
  top: string;
  right: string;
  bottom: string;
  left: string;
}

const EMPTY_MARKERS: EdgeMarkers = { top: '', right: '', bottom: '', left: '' };
const QUARTER_TURN = 90;
const FULL_TURN = 360;

/**
 * Edge-middle orientation letters from Image Orientation (Patient) row / column cosines, after the
 * viewport flips (applied in image space) and rotation (clockwise, in quarter turns).
 */
export function orientationMarkers(orientation: number[], rotation: number, hflip: boolean, vflip: boolean): EdgeMarkers {
  if (orientation.length < 6 || orientation.every((v) => v === 0)) return EMPTY_MARKERS;
  const { getOrientationString, invertOrientationString } = window.cornerstoneTools.orientation;
  const row = getOrientationString(orientation.slice(0, 3));
  const column = getOrientationString(orientation.slice(3, 6));
  let markers: EdgeMarkers = { top: invertOrientationString(column), bottom: column, left: invertOrientationString(row), right: row };
  if (hflip) markers = { ...markers, left: markers.right, right: markers.left };
  if (vflip) markers = { ...markers, top: markers.bottom, bottom: markers.top };
  const turns = Math.round((((rotation % FULL_TURN) + FULL_TURN) % FULL_TURN) / QUARTER_TURN) % 4;
  for (let i = 0; i < turns; i++) {
    markers = { top: markers.left, right: markers.top, bottom: markers.right, left: markers.bottom };
  }
  return markers;
}

function currentOrientation(viewport: Viewport, reader: TagReader): number[] {
  const fromTags = reader.numbers('00200037');
  if (fromTags.length >= 6) return fromTags;
  const image = viewport.currentImage();
  const plane = image ? (window.cornerstone.metaData.get('imagePlaneModule', image.imageId) as { imageOrientationPatient?: number[] } | undefined) : undefined;
  return plane?.imageOrientationPatient?.map(Number) ?? [];
}

// --- DOM ----------------------------------------------------------------------------------------

function slot(root: HTMLElement, attribute: 'corner' | 'edge', key: string): HTMLElement {
  let el = root.querySelector<HTMLElement>(`[data-${attribute}="${key}"]`);
  if (!el) {
    el = document.createElement('div');
    el.dataset[attribute] = key;
    el.className = attribute === 'corner' ? `vl-corner vl-${key}` : `vl-orientation vl-orientation-${key}`;
    root.appendChild(el);
  }
  return el;
}

function lineElement(line: Line): HTMLElement {
  const row = document.createElement('div');
  row.className = 'vl-line';
  if (typeof line === 'string') {
    row.textContent = line;
    return row;
  }
  const label = document.createElement('b');
  label.textContent = line.label;
  row.append(label, document.createTextNode(line.value));
  return row;
}

function lineKey(line: Line): string {
  return typeof line === 'string' ? line : `${line.label}\u0001${line.value}`;
}

function fillCorner(el: HTMLElement, lines: Line[]): void {
  const key = lines.map(lineKey).join('\u0002');
  if (el.dataset.key === key) return;
  el.dataset.key = key;
  el.replaceChildren(...lines.map(lineElement));
}

/**
 * Corner and edge text of one viewport (spec section 3): series description / number, technical
 * lines and custom tags top-left; custom tags top-right; projection warning, slice counter and
 * thickness (or CINE) bottom-left; W/L bottom-right; orientation letters at the edge middles.
 */
export function renderViewLabels(viewport: Viewport): void {
  const image = viewport.currentImage();
  const vp = window.cornerstone.getViewport(viewport.element);
  if (!viewport.currentStack || !image || !vp) return;
  known.add(viewport);
  const root = viewport.overlayRoot;
  root.classList.toggle('hidden', !overlayVisible);
  const reader = tagReaderFor(viewport);
  const built = reader && buildCorners(viewport, reader);
  const corners = built && withExtraLines(viewport, built);
  if (!reader || !corners) return;
  for (const key of Object.keys(corners) as Array<keyof Corners>) fillCorner(slot(root, 'corner', key), corners[key]);
  if (overlayVisible) placeLabels(viewport, root);
  const markers = orientationMarkers(currentOrientation(viewport, reader), vp.rotation, vp.hflip, vp.vflip);
  for (const edge of Object.keys(markers) as Array<keyof EdgeMarkers>) {
    const el = slot(root, 'edge', edge);
    if (el.textContent !== markers[edge]) el.textContent = markers[edge];
  }
}

// --- Labels in the free side (labelplace.ts) ---------------------------------------------------

const labelSides = new WeakMap<HTMLElement, LabelSide | null>();
/** What the last placement was computed from: skip the layout reads when none of it changed (cine, W/L drags). */
const placementKeys = new WeakMap<HTMLElement, string>();

/** The image's box on screen in the cell's CSS pixels (its four corners through the viewport transform). */
function imageBox(viewport: Viewport): { left: number; right: number } | undefined {
  const enabled = window.cornerstone.getEnabledElement(viewport.element);
  const image = enabled.image;
  if (!image || !enabled.canvas.width) return undefined;
  const ratio = enabled.canvas.clientWidth / enabled.canvas.width;
  const xs = [{ x: 0, y: 0 }, { x: image.columns, y: 0 }, { x: 0, y: image.rows }, { x: image.columns, y: image.rows }]
    .map((p) => window.cornerstone.pixelToCanvas(viewport.element, p).x * ratio);
  return { left: Math.min(...xs), right: Math.max(...xs) };
}

function placeLabels(viewport: Viewport, root: HTMLElement): void {
  const box = imageBox(viewport);
  const corner = (key: keyof Corners) => slot(root, 'corner', key);
  const content = (['topLeft', 'topRight', 'bottomLeft', 'bottomRight'] as const).map((key) => corner(key).dataset.key ?? '').join('\u0003');
  const key = `${Math.round(box?.left ?? -1)}|${Math.round(box?.right ?? -1)}|${root.clientWidth}|${content}`;
  if (placementKeys.get(root) === key) return;
  placementKeys.set(root, key);
  const layout: LabelLayout | undefined = box && {
    cellWidth: root.clientWidth,
    imageLeft: box.left,
    imageRight: box.right,
    leftBlock: Math.max(corner('topLeft').offsetWidth, corner('bottomLeft').offsetWidth),
    rightBlock: Math.max(corner('topRight').offsetWidth, corner('bottomRight').offsetWidth),
  };
  const side = layout ? chooseLabelSide(layout, labelSides.get(root) ?? null) : null;
  labelSides.set(root, side);
  if (!side || !layout) {
    delete root.dataset.labelSide;
    return;
  }
  root.dataset.labelSide = side;
  const top = side === 'left' ? 'topLeft' : 'topRight';
  const bottom = side === 'left' ? 'bottomLeft' : 'bottomRight';
  root.style.setProperty('--vl-free-width', `${Math.floor(freeWidth(layout, side))}px`);
  root.style.setProperty('--vl-top-stack', `${corner(top).offsetHeight}px`);
  root.style.setProperty('--vl-bottom-stack', `${corner(bottom).offsetHeight}px`);
}

// --- Scale bar (scaleOverlay: true) -------------------------------------------------------------

const SCALE_TOOL_NAME = 'ScaleOverlay';
const scaleState = new WeakMap<HTMLElement, boolean>();

function hasPixelSpacing(element: HTMLElement): boolean {
  const image = window.cornerstone.getEnabledElement(element).image;
  if (!image) return false;
  const plane = window.cornerstone.metaData.get('imagePlaneModule', image.imageId) as
    | { rowPixelSpacing?: number; columnPixelSpacing?: number; rowImagePixelSpacing?: number; colImagePixelSpacing?: number }
    | undefined;
  const row = plane?.rowPixelSpacing || plane?.rowImagePixelSpacing || image.rowPixelSpacing;
  const column = plane?.columnPixelSpacing || plane?.colImagePixelSpacing || image.columnPixelSpacing;
  return Boolean(row && column);
}

/** The scale bar only makes sense (and only renders without warnings) on calibrated images. */
function updateScaleOverlay(element: HTMLElement): void {
  const want = hasPixelSpacing(element);
  if (scaleState.get(element) === want) return;
  const api = initTools(window.VIEWER_CONFIG.magnifyFactor);
  if (want) api.setToolEnabledForElement(element, SCALE_TOOL_NAME);
  else api.setToolDisabledForElement(element, SCALE_TOOL_NAME);
  scaleState.set(element, want);
}

function attachScaleOverlay(element: HTMLElement): void {
  const toolClass = window.cornerstoneTools.ScaleOverlayTool;
  if (!toolClass || scaleState.has(element)) return;
  initTools(window.VIEWER_CONFIG.magnifyFactor).addToolForElement(element, toolClass);
  scaleState.set(element, false);
  element.addEventListener(window.cornerstone.EVENTS.NEW_IMAGE, () => {
    try {
      updateScaleOverlay(element);
    } catch (error) {
      console.warn('[viewer] scale overlay unavailable', error);
    }
  });
}

/** Enables the scale bar on every viewport (IMEXHS config `scaleOverlay: true`, not user-toggleable). */
export function install(shell: ViewerShell): void {
  const attachAll = () => {
    for (const viewport of shell.viewports()) {
      try {
        attachScaleOverlay(viewport.element);
      } catch (error) {
        console.warn('[viewer] scale overlay unavailable', error);
      }
    }
  };
  shell.on('layout-changed', attachAll);
  attachAll();
}
